/* ==========================================================================
   transactionService

   REAL MODE: reads the `transactions` table, filtered explicitly by
   estateContext.activeEstateId (see meterService's header comment for
   why). Wallet funding, electricity purchases, bill payments, and
   withdrawals all write to this same table via the Postgres functions in
   supabase/schema.sql — this file only ever reads it, matching mock
   mode's "one place the UI reads history from".

   Cursor-based pagination isn't implemented for real mode yet — `limit`
   works, `cursor` is accepted but ignored. Fine for a single estate's
   transaction volume; worth adding (using created_at + id as the cursor)
   if this ever needs to page through a large history.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mapTransactionRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay } from "./mock/mockUtils";
import { requireActiveEstateId } from "../state/estateContext";
import { sessionStore } from "../state/session";
import type { TransactionPage, TransactionQuery } from "../types";

async function listTransactions(query: TransactionQuery = {}): Promise<TransactionPage> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(400);
    let items = mockDb.getTransactions(estateId);
    if (query.types && query.types.length > 0) {
      items = items.filter((t) => query.types!.includes(t.type));
    }
    const limit = query.limit ?? items.length;
    return { items: items.slice(0, limit), nextCursor: null };
  }

  // Only this person's own activity.
  const userId = sessionStore.user?.id;
  if (!userId) throw new Error("Please sign in again.");
  let request = getSupabase().from("transactions").select("*").eq("estate_id", estateId).eq("user_id", userId).order("created_at", { ascending: false });
  if (query.types && query.types.length > 0) request = request.in("type", query.types);
  if (query.limit) request = request.limit(query.limit);

  const { data, error } = await request;
  if (error) throw new Error(error.message);
  return { items: (data ?? []).map(mapTransactionRow), nextCursor: null };
}

export const transactionService = { listTransactions };
